import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, Clock, Copy, Mail, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { cn } from "@/lib/utils/cn";

import { actionPlanKeys } from "../api/keys";
import {
  patchStakeholderGroup,
  useConsultations,
  useCreateStakeholderGroup,
  useDeleteStakeholderGroup,
  useStakeholderGroups,
  useToggleConsultation,
} from "../api/hooks";
import type {
  ActionPlanConsultation,
  ActionPlanDetail,
  StakeholderGroup,
} from "../api/types";
import { apiErrorDetail } from "../lib/errors";
import { Badge } from "./ui/Badge";
import { ConsultationEmailEditor } from "./ConsultationEmailEditor";

/** ~30s per assigned action, minimum 1 minute (matches the legacy estimate). */
function estimateMinutes(actionCount: number): number {
  return Math.max(1, Math.ceil(actionCount * 0.5));
}

/** Count of this plan's selected actions assigned to each stakeholder group. */
function assignedCounts(plan: ActionPlanDetail): Map<string, number> {
  const counts = new Map<string, number>();
  for (const thematic of plan.action_plan_thematics) {
    for (const issue of thematic.action_plan_issues) {
      for (const goal of issue.action_plan_goals) {
        for (const action of goal.action_plan_actions) {
          if (!action.is_selected) continue;
          for (const groupId of action.consultation_groups ?? []) {
            counts.set(groupId, (counts.get(groupId) ?? 0) + 1);
          }
        }
      }
    }
  }
  return counts;
}

/**
 * Stakeholder-consultation manager, opened from the "Consulter les parties
 * prenantes" button on step 2 — mirrors the old app's share modal. Lists the
 * enterprise's stakeholder categories; each category with ≥1 assigned selected
 * action has a live survey link (copy), open/close toggle, response count +
 * time estimate, and an email composer (nested modal). Categories can be added,
 * renamed and deleted here. No manual "start" — a consultation exists as soon as
 * a category is assigned to an action (the server auto-syncs it).
 */
export function ConsultationModal({
  planId,
  plan,
  onClose,
}: {
  planId: string;
  plan: ActionPlanDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const groups = useStakeholderGroups();
  const consultations = useConsultations(planId);
  const createGroup = useCreateStakeholderGroup();
  const [newName, setNewName] = useState("");
  const [emailFor, setEmailFor] = useState<{ id: string; name: string } | null>(null);

  const counts = useMemo(() => assignedCounts(plan), [plan]);
  const consultationByGroup = useMemo(() => {
    const map = new Map<string, ActionPlanConsultation>();
    for (const c of consultations.data ?? []) map.set(c.stakeholder_group, c);
    return map;
  }, [consultations.data]);

  const addGroup = () => {
    const name = newName.trim();
    if (!name) return;
    createGroup.mutate(
      { name },
      {
        onSuccess: () => setNewName(""),
        onError: (err) =>
          toast.error(apiErrorDetail(err) ?? t("actionPlans.errors.saveFailed")),
      },
    );
  };

  return (
    <Modal title={t("actionPlans.consultations.modalTitle")} onClose={onClose}>
      <p className="mb-3 text-sm text-muted-foreground">
        {t("actionPlans.consultations.modalHelp")}
      </p>

      {groups.data && groups.data.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t("actionPlans.consultations.noGroups")}
        </p>
      )}

      <ul className="space-y-2">
        {groups.data?.map((group) => (
          <GroupRow
            key={group.id}
            group={group}
            consultation={consultationByGroup.get(group.id)}
            assignedCount={counts.get(group.id) ?? 0}
            planId={planId}
            onEmail={() => setEmailFor({ id: group.id, name: group.name })}
          />
        ))}
      </ul>

      <div className="mt-4 flex items-end gap-2 border-t border-border pt-4">
        <div className="flex-1">
          <Input
            aria-label={t("actionPlans.consultations.newCategory")}
            placeholder={t("actionPlans.consultations.newCategoryPlaceholder")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addGroup();
              }
            }}
          />
        </div>
        <Button type="button" onClick={addGroup} disabled={createGroup.isPending}>
          <Plus aria-hidden />
          {t("actionPlans.consultations.addCategory")}
        </Button>
      </div>

      {emailFor && (
        <Modal
          title={`${t("actionPlans.consultations.email")} — ${emailFor.name}`}
          onClose={() => setEmailFor(null)}
        >
          <ConsultationEmailEditor
            consultationId={consultationByGroup.get(emailFor.id)?.id ?? ""}
            onSent={() => setEmailFor(null)}
          />
        </Modal>
      )}
    </Modal>
  );
}

function GroupRow({
  group,
  consultation,
  assignedCount,
  planId,
  onEmail,
}: {
  group: StakeholderGroup;
  consultation: ActionPlanConsultation | undefined;
  assignedCount: number;
  planId: string;
  onEmail: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toggle = useToggleConsultation(planId);
  const deleteGroup = useDeleteStakeholderGroup();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [intro, setIntro] = useState(group.intro_message ?? "");
  const [saving, setSaving] = useState(false);

  const saveEdit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await patchStakeholderGroup(group.id, {
        name: name.trim(),
        intro_message: intro,
      });
      await qc.invalidateQueries({ queryKey: actionPlanKeys.groups() });
      setEditing(false);
    } catch (err) {
      toast.error(apiErrorDetail(err) ?? t("actionPlans.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!consultation) return;
    try {
      await navigator.clipboard.writeText(consultation.survey_url);
      toast.success(t("actionPlans.consultations.linkCopied"));
    } catch {
      toast.error(t("actionPlans.consultations.linkCopyFailed"));
    }
  };

  if (editing) {
    return (
      <li className="space-y-2 rounded-md border border-border p-3">
        <Input
          aria-label={t("actionPlans.groups.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <textarea
          aria-label={t("actionPlans.groups.introMessage")}
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          rows={2}
          placeholder={t("actionPlans.groups.introMessage")}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
            {t("actionPlans.consultations.cancel")}
          </Button>
          <Button size="sm" onClick={() => void saveEdit()} disabled={saving}>
            <Check aria-hidden />
            {t("actionPlans.consultations.save")}
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3">
      <div className="min-w-0 space-y-1">
        <p className="font-medium">{group.name}</p>
        {consultation ? (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge tone={consultation.is_open ? "success" : "neutral"}>
              {consultation.is_open
                ? t("actionPlans.consultations.open")
                : t("actionPlans.consultations.closed")}
            </Badge>
            <span>
              {t("actionPlans.consultations.responses", {
                count: Number(consultation.response_count),
              })}
            </span>
            {assignedCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <Clock className="size-3.5" aria-hidden />
                {t("actionPlans.consultations.timeEstimate", {
                  count: assignedCount,
                  minutes: estimateMinutes(assignedCount),
                })}
              </span>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t("actionPlans.consultations.assignToEnable")}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {consultation && (
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => void copyLink()}>
              <Copy aria-hidden />
              {t("actionPlans.consultations.copyLink")}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onEmail}>
              <Mail aria-hidden />
              {t("actionPlans.consultations.email")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                toggle.mutate({ id: consultation.id, is_open: !consultation.is_open })
              }
              disabled={toggle.isPending}
            >
              {consultation.is_open
                ? t("actionPlans.consultations.close")
                : t("actionPlans.consultations.reopen")}
            </Button>
          </>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("actionPlans.consultations.rename")}
          title={t("actionPlans.consultations.rename")}
          onClick={() => {
            setName(group.name);
            setIntro(group.intro_message ?? "");
            setEditing(true);
          }}
        >
          <Pencil />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("actionPlans.groups.delete")}
          title={t("actionPlans.groups.delete")}
          className={cn("text-destructive")}
          onClick={() => deleteGroup.mutate(group.id)}
          disabled={deleteGroup.isPending}
        >
          <Trash2 />
        </Button>
      </div>
    </li>
  );
}
